import { useEffect, useState } from "react";
import { Copy, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

// Attivazione 2FA (TOTP): QR/chiave manuale → conferma codice → codici di recupero
// (mostrati UNA volta). Con preToken = attivazione obbligatoria admin al login
// (il backend risponde con token+user completi alla conferma).
export function TwoFactorSetup({ preToken = null, forced = false, onDone, onCancel }) {
  const { t } = useLanguage();
  const [step, setStep] = useState("loading"); // loading → qr → codes
  const [qr, setQr] = useState(null);
  const [manualKey, setManualKey] = useState("");
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const headers = preToken ? { Authorization: `Bearer ${preToken}` } : undefined;

  useEffect(() => {
    api.post("/2fa/setup", {}, { headers })
      .then(({ data }) => {
        setQr(data.data.qr_data_url);
        setManualKey(data.data.manual_key);
        setStep("qr");
      })
      .catch(() => { toast.error(t("misc.error")); onCancel?.(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/2fa/setup/confirm", { code: code.trim() }, { headers });
      setCodes(data.data.recovery_codes || []);
      setStep("codes");
      if (data.data.token) {
        // flusso admin obbligatorio: login completato
        onDone(data.data);
      }
    } catch (err) {
      const d = err.response?.data?.detail || "";
      toast.error(d === "2fa_setup_expired" ? t("tfa.setupExpired") : t("tfa.invalidCode"));
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = () => {
    navigator.clipboard?.writeText((codes || []).join("\n")).then(() => toast.success(t("tfa.codesCopied")));
  };

  if (step === "loading") {
    return <div data-testid="twofa-setup-loading" className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>;
  }

  if (step === "codes") {
    return (
      <div data-testid="twofa-recovery-codes" className="space-y-4">
        <div className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {t("tfa.recoveryWarning")}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {codes.map((c) => (
            <code key={c} data-testid="twofa-recovery-code" className="rounded-xl bg-slate-900 text-emerald-300 text-center py-2 font-mono-t text-sm tracking-[0.2em]">{c}</code>
          ))}
        </div>
        <div className="flex gap-2">
          <button type="button" data-testid="twofa-copy-codes" onClick={copyCodes}
            className="flex-1 rounded-full border border-[var(--border-strong)] px-5 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
            <Copy className="inline w-3.5 h-3.5 mr-1" /> {t("tfa.copyCodes")}
          </button>
          <button type="button" data-testid="twofa-setup-done" onClick={() => onDone(null)}
            className="flex-1 rounded-full bg-[#FF6B35] text-white px-5 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#e55a2b] transition-colors">
            {t("tfa.codesSaved")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form data-testid="twofa-setup" onSubmit={confirm} className="space-y-4">
      {forced && (
        <div className="rounded-2xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 shrink-0" /> {t("tfa.forcedAdmin")}
        </div>
      )}
      <p className="text-sm text-[var(--text-secondary)]">{t("tfa.scanQr")}</p>
      <div className="rounded-3xl bg-white p-4 border border-[var(--border-subtle)] w-fit mx-auto">
        <img src={qr} alt="QR 2FA" data-testid="twofa-qr" className="w-48 h-48" />
      </div>
      <div className="text-center">
        <div className="font-mono-t text-[9px] uppercase tracking-widest text-[var(--text-muted)]">{t("tfa.manualKey")}</div>
        <code data-testid="twofa-manual-key" className="font-mono-t text-sm text-[#FF6B35] tracking-[0.15em] select-all">{manualKey}</code>
      </div>
      <input data-testid="twofa-setup-code" value={code} required autoFocus inputMode="numeric" autoComplete="one-time-code"
        maxLength={6} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000"
        className="w-full rounded-2xl border border-[var(--border-strong)] bg-transparent px-5 py-3 text-center font-mono-t text-2xl tracking-[0.4em] outline-none focus:border-[#FF6B35] transition-colors" />
      <button data-testid="twofa-setup-confirm" type="submit" disabled={busy || code.length !== 6}
        className="w-full rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-6 py-3.5 font-mono-t text-xs uppercase tracking-[0.2em] hover:bg-[#FF6B35] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {t("tfa.confirmEnable")}
      </button>
      {!forced && onCancel && (
        <button type="button" data-testid="twofa-setup-cancel" onClick={onCancel}
          className="w-full text-center text-sm text-[var(--text-secondary)] hover:underline">{t("tfa.cancel")}</button>
      )}
    </form>
  );
}
